import { Check, ShieldCheck, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { SessionStepDto } from '../../schemas/sessionSchemas';
import { formatResultDateTime } from '../../sessionDateTime';

// Canonical PAN Return-Data fields (PDF §4.5), in display order. The stored
// result is the NORMALISED, filtered object (see PanResultNormalizer +
// ApplyReturnFieldFilter on the backend), so we render exactly the keys present
// — i.e. the fields the admin left enabled in Return Data. Values are already
// mapped to these keys regardless of which API ran (Verify / Lite / OCR).
const PAN_FIELD_LABELS: Array<{ key: string; label: string }> = [
  { key: 'pan_number', label: 'PAN Number' },
  { key: 'pan_status', label: 'PAN Status' },
  { key: 'pan_holder_name', label: 'PAN Holder Name' },
  { key: 'date_of_birth', label: 'Date of Birth' },
  { key: 'pan_category', label: 'PAN Category' },
  { key: 'age', label: 'Age' },
  { key: 'name_match_score', label: 'Name Match Score' },
  { key: 'duplicate_pan_flag', label: 'Duplicate PAN Flag' },
  { key: 'verification_status', label: 'Status' },
  { key: 'verified_at', label: 'Verified At' },
  { key: 'method_used', label: 'Method Used' },
  { key: 'verification_attempts', label: 'Verification Attempts' },
  { key: 'session_id', label: 'Session ID' },
];

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (key === 'duplicate_pan_flag') return value === true || value === 'true' ? 'Yes' : 'No';
  if (key === 'name_match_score') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : `${n.toFixed(0)}%`;
  }
  if (key === 'verified_at') {
    return formatResultDateTime(value);
  }
  return String(value);
}

export function PanResultBlock({ step }: { step: SessionStepDto }) {
  const rows: Array<{ label: string; value: string }> = [];

  if (step.resultJson) {
    let parsed: Record<string, unknown> | null = null;
    try {
      const p = JSON.parse(step.resultJson);
      parsed = p && typeof p === 'object' ? (p as Record<string, unknown>) : null;
    } catch {
      parsed = null;
    }
    if (parsed) {
      for (const { key, label } of PAN_FIELD_LABELS) {
        if (key in parsed && parsed[key] !== null && parsed[key] !== '') {
          rows.push({ label, value: formatValue(key, parsed[key]) });
        }
      }
    }
  }

  if (rows.length === 0) return null;

  const passed = step.status === 'Passed';
  const review = step.status === 'Review';
  // riskOutcome="Reject" means an Advanced Rule fired — show "Rejected by policy".
  const rejected = !passed && !review && step.riskOutcome === 'Reject';

  const headerLabel = passed
    ? 'Verified'
    : review
      ? 'Under Review'
      : rejected
        ? 'Rejected by policy'
        : 'Verification Failed';

  return (
    <div
      className={cn(
        'mt-3 rounded-xl p-4',
        passed && 'bg-[#F0FDF4]',
        review && 'bg-[#FFFBEB]',
        !passed && !review && 'bg-[#FEF2F2]',
      )}
    >
      <div className="mb-3 flex items-center gap-2">
        {passed && <Check size={16} className="text-[#00C232]" />}
        {review && <ShieldCheck size={16} className="text-[#DA7400]" />}
        {!passed && !review && <XCircle size={16} className="text-[#BF3636]" />}
        <span
          className={cn(
            'text-[14px] font-semibold',
            passed && 'text-[#00C232]',
            review && 'text-[#DA7400]',
            !passed && !review && 'text-[#BF3636]',
          )}
        >
          {headerLabel}
        </span>
      </div>
      <div
        className={cn(
          'divide-y',
          passed && 'divide-[#BBF7D0]',
          review && 'divide-[#FDE68A]',
          !passed && !review && 'divide-[#FECACA]',
        )}
      >
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between py-2">
            <span className="text-[13px] text-[#666666]">{r.label}</span>
            <span className="text-[13px] font-semibold text-[#303030]">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
